import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Typography,
} from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import intercompanyService from '@/services/intercompany.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const IntercompanyConsolidationPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatCurrency } = useCurrency();
  const [groupId, setGroupId] = useState<string | undefined>(searchParams.get('group') || undefined);
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('year'), dayjs()]);

  const { data: groups = [] } = useQuery({
    queryKey: ['intercompany-groups'],
    queryFn: () => intercompanyService.listGroups(),
  });

  useEffect(() => {
    if (!groupId && groups.length === 1) setGroupId(groups[0].id);
  }, [groups, groupId]);

  useEffect(() => {
    if (groupId) setSearchParams({ group: groupId }, { replace: true });
  }, [groupId, setSearchParams]);

  const asOfStr = asOf.format('YYYY-MM-DD');
  const fromStr = range[0].format('YYYY-MM-DD');
  const toStr = range[1].format('YYYY-MM-DD');

  const worksheetQ = useQuery({
    queryKey: ['ic-elim', groupId, asOfStr, fromStr],
    queryFn: () =>
      intercompanyService.eliminationWorksheet(groupId!, { as_of_date: asOfStr, from_date: fromStr }),
    enabled: !!groupId,
  });

  const tbQ = useQuery({
    queryKey: ['ic-consol-tb', groupId, asOfStr, fromStr],
    queryFn: () =>
      intercompanyService.consolidatedTrialBalance(groupId!, {
        as_of_date: asOfStr,
        from_date: fromStr,
      }),
    enabled: !!groupId,
  });

  const plQ = useQuery({
    queryKey: ['ic-consol-pl', groupId, fromStr, toStr],
    queryFn: () =>
      intercompanyService.consolidatedProfitLoss(groupId!, { from_date: fromStr, to_date: toStr }),
    enabled: !!groupId,
  });

  const bsQ = useQuery({
    queryKey: ['ic-consol-bs', groupId, asOfStr],
    queryFn: () => intercompanyService.consolidatedBalanceSheet(groupId!, { as_of_date: asOfStr }),
    enabled: !!groupId,
  });

  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: g.id, label: `${g.group_code} — ${g.group_name}` })),
    [groups]
  );

  const [activeTab, setActiveTab] = useState('elim');

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('groupConsolidationTitle')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('groupConsolidationSubtitle')}
          </Paragraph>
        </div>
        <Space>
          <ReportDocumentActions
            reportKey="intercompany-consolidation"
            params={groupId ? { group_id: groupId, view: activeTab, as_of: asOfStr, from: fromStr, to: toStr } : null}
          />
          <Button onClick={() => navigate('/fin/intercompany/groups')}>{t('manageGroups')}</Button>
        </Space>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Text type="secondary">{t('consolidationGroup')}</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder={t('selectGroup')}
              value={groupId}
              options={groupOptions}
              onChange={setGroupId}
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>
            <Text type="secondary">{t('asOfPeriodEnd')}</Text>
            <DatePicker style={{ width: '100%', marginTop: 4 }} value={asOf} onChange={(d) => d && setAsOf(d)} />
          </Col>
          <Col xs={24} md={8}>
            <Text type="secondary">{t('plTbPeriod')}</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 4 }}
              value={range}
              onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
            />
          </Col>
        </Row>
      </Card>

      {!groupId ? (
        <Alert
          type="info"
          showIcon
          message={t('selectOrCreateConsolidationGroup')}
          action={
            <Button size="small" onClick={() => navigate('/fin/intercompany/groups')}>
              {t('groups')}
            </Button>
          }
        />
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'elim',
              label: t('eliminationWorksheet'),
              children: (
                <>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col>
                      <Statistic
                        title={t('totalEliminationAmount')}
                        value={worksheetQ.data?.total_eliminations ?? 0}
                        formatter={(v) => formatCurrency(Number(v))}
                      />
                    </Col>
                  </Row>
                  <Table
                    loading={worksheetQ.isLoading}
                    rowKey={(_, i) => String(i)}
                    dataSource={worksheetQ.data?.lines ?? []}
                    columns={[
                      { title: t('type'), dataIndex: 'elimination_type', width: 160 },
                      { title: t('description'), dataIndex: 'description' },
                      { title: t('debitAcct'), dataIndex: 'debit_account_code', width: 110 },
                      { title: t('creditAcct'), dataIndex: 'credit_account_code', width: 110 },
                      {
                        title: t('amount'),
                        dataIndex: 'amount',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                    ]}
                    locale={{ emptyText: t('noEliminationsForPeriod') }}
                  />
                </>
              ),
            },
            {
              key: 'tb',
              label: t('consolidatedTb'),
              children: (
                <>
                  {tbQ.data && (
                    <Alert
                      style={{ marginBottom: 12 }}
                      type={tbQ.data.is_balanced ? 'success' : 'warning'}
                      showIcon
                      message={
                        tbQ.data.is_balanced
                          ? t('consolTbInBalance')
                          : t('consolTbOutOfBalance')
                      }
                    />
                  )}
                  <Table
                    loading={tbQ.isLoading}
                    size="small"
                    rowKey="account_code"
                    dataSource={tbQ.data?.rows ?? []}
                    scroll={{ x: 1100 }}
                    columns={[
                      { title: t('code'), dataIndex: 'account_code', width: 90, fixed: 'left' },
                      { title: t('account'), dataIndex: 'account_name', width: 200 },
                      {
                        title: t('combinedDr'),
                        dataIndex: 'combined_debit',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                      {
                        title: t('combinedCr'),
                        dataIndex: 'combined_credit',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                      {
                        title: t('elimDr'),
                        dataIndex: 'elimination_debit',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                      {
                        title: t('elimCr'),
                        dataIndex: 'elimination_credit',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                      {
                        title: t('consolDr'),
                        dataIndex: 'consolidated_debit',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                      {
                        title: t('consolCr'),
                        dataIndex: 'consolidated_credit',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                    ]}
                    summary={() =>
                      tbQ.data ? (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2}>
                            <strong>{t('totals')}</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            {formatCurrency(tbQ.data.total_combined_debit)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            {formatCurrency(tbQ.data.total_combined_credit)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} colSpan={2} />
                          <Table.Summary.Cell index={6} align="right">
                            {formatCurrency(tbQ.data.total_consolidated_debit)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7} align="right">
                            {formatCurrency(tbQ.data.total_consolidated_credit)}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      ) : null
                    }
                  />
                </>
              ),
            },
            {
              key: 'pl',
              label: t('consolidatedPl'),
              children: (
                <>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col>
                      <Statistic
                        title={t('netProfit')}
                        value={plQ.data?.net_profit ?? 0}
                        formatter={(v) => formatCurrency(Number(v))}
                      />
                    </Col>
                    <Col>
                      <Statistic
                        title={t('icRevExpEliminated')}
                        value={plQ.data?.ic_revenue_expense_eliminated ?? 0}
                        formatter={(v) => formatCurrency(Number(v))}
                      />
                    </Col>
                  </Row>
                  <Table
                    loading={plQ.isLoading}
                    rowKey={(r) => `${r.section}-${r.account_code}-${r.account_name}`}
                    dataSource={[
                      ...(plQ.data?.revenue.accounts.map((a) => ({ ...a, section: t('sectionRevenue') })) ?? []),
                      ...(plQ.data
                        ? [
                            {
                              account_code: '',
                              account_name: t('totalRevenue'),
                              amount: plQ.data.revenue.subtotal,
                              section: t('sectionRevenue'),
                            },
                          ]
                        : []),
                      ...(plQ.data?.expenses.accounts.map((a) => ({ ...a, section: t('sectionExpenses') })) ?? []),
                      ...(plQ.data
                        ? [
                            {
                              account_code: '',
                              account_name: t('totalExpenses'),
                              amount: plQ.data.expenses.subtotal,
                              section: t('sectionExpenses'),
                            },
                            {
                              account_code: '',
                              account_name: t('netProfit'),
                              amount: plQ.data.net_profit,
                              section: t('sectionResult'),
                            },
                          ]
                        : []),
                    ]}
                    columns={[
                      { title: t('section'), dataIndex: 'section', width: 120 },
                      { title: t('code'), dataIndex: 'account_code', width: 90 },
                      { title: t('account'), dataIndex: 'account_name' },
                      {
                        title: t('amount'),
                        dataIndex: 'amount',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'bs',
              label: t('consolidatedBs'),
              children: (
                <>
                  {bsQ.data && (
                    <Alert
                      style={{ marginBottom: 12 }}
                      type={bsQ.data.is_balanced ? 'success' : 'warning'}
                      showIcon
                      message={
                        bsQ.data.is_balanced
                          ? t('assetsEqualLE')
                          : t('bsDoesNotBalance')
                      }
                    />
                  )}
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col>
                      <Statistic
                        title={t('totalAssets')}
                        value={bsQ.data?.total_assets ?? 0}
                        formatter={(v) => formatCurrency(Number(v))}
                      />
                    </Col>
                    <Col>
                      <Statistic
                        title={t('totalLE')}
                        value={bsQ.data?.total_liabilities_and_equity ?? 0}
                        formatter={(v) => formatCurrency(Number(v))}
                      />
                    </Col>
                    <Col>
                      <Statistic
                        title={t('retainedEarningsYtd')}
                        value={bsQ.data?.retained_earnings ?? 0}
                        formatter={(v) => formatCurrency(Number(v))}
                      />
                    </Col>
                  </Row>
                  <Table
                    loading={bsQ.isLoading}
                    rowKey={(r) => `${r.section}-${r.account_code}-${r.account_name}`}
                    dataSource={[
                      ...(bsQ.data?.assets.accounts.map((a) => ({ ...a, section: t('sectionAssets') })) ?? []),
                      ...(bsQ.data
                        ? [{ account_code: '', account_name: t('totalAssets'), amount: bsQ.data.total_assets, section: t('sectionAssets') }]
                        : []),
                      ...(bsQ.data?.liabilities.accounts.map((a) => ({ ...a, section: t('sectionLiabilities') })) ?? []),
                      ...(bsQ.data?.equity.accounts.map((a) => ({ ...a, section: t('sectionEquity') })) ?? []),
                      ...(bsQ.data
                        ? [
                            {
                              account_code: '',
                              account_name: t('retainedEarningsYtd'),
                              amount: bsQ.data.retained_earnings,
                              section: t('sectionEquity'),
                            },
                            {
                              account_code: '',
                              account_name: t('totalLiabilitiesAndEquity'),
                              amount: bsQ.data.total_liabilities_and_equity,
                              section: t('sectionEquity'),
                            },
                          ]
                        : []),
                    ]}
                    columns={[
                      { title: t('section'), dataIndex: 'section', width: 120 },
                      { title: t('code'), dataIndex: 'account_code', width: 90 },
                      { title: t('account'), dataIndex: 'account_name' },
                      {
                        title: t('amount'),
                        dataIndex: 'amount',
                        align: 'right',
                        render: (v) => formatCurrency(Number(v)),
                      },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default IntercompanyConsolidationPage;
